/**
 * FrameworkAnalysisTab — Cross-framework comparison
 * Divergent treatment cards + full mapping table
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, safeFetch } from '../../constants';

const KE = `${API_V1}/knowledge-engine`;

const th: React.CSSProperties = {
  textAlign: 'left' as const, padding: '8px 10px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-subtle)',
  textTransform: 'uppercase' as const, letterSpacing: 0.5, cursor: 'pointer',
};
const td = (i: number): React.CSSProperties => ({
  padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-subtle)',
  background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
});
const mono: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};

interface Mapping {
  us_gaap_element: string;
  ifrs_element: string;
  mapping_type: string;
  affects_je: boolean;
  divergence_notes?: string;
}

export default function FrameworkAnalysisTab() {
  const [divergent, setDivergent] = useState<Mapping[]>([]);
  const [allMappings, setAllMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState('us_gaap_element');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      safeFetch(`${KE}/xbrl/mappings/divergent`, { mappings: [] }),
      // The graph endpoint includes maps_to edges for all mappings
      authFetch(`${KE}/graph?types=xbrl_concept`).then(r => r.ok ? r.json() : { edges: [] }).catch(() => ({ edges: [] })),
    ]).then(([divRes, graphRes]) => {
      const divMappings = divRes?.mappings || [];
      setDivergent(divMappings);

      // Build all mappings from graph maps_to edges + divergent data
      const mapsToEdges = (graphRes?.edges || []).filter((e: any) => e.relationship === 'maps_to');
      const allMap: Mapping[] = [];
      const seen = new Set<string>();

      // Add divergent ones first
      for (const m of divMappings) {
        const key = `${m.us_gaap_element}:${m.ifrs_element}`;
        if (!seen.has(key)) {
          seen.add(key);
          allMap.push(m);
        }
      }

      // Add from graph edges
      for (const e of mapsToEdges) {
        const usEl = e.source?.replace('xbrl:US_GAAP:', '') || '';
        const ifrsEl = e.target?.replace('xbrl:IFRS:', '') || '';
        const key = `${usEl}:${ifrsEl}`;
        if (!seen.has(key) && usEl && ifrsEl) {
          seen.add(key);
          allMap.push({
            us_gaap_element: usEl,
            ifrs_element: ifrsEl,
            mapping_type: e.metadata?.mapping_type || 'equivalent',
            affects_je: e.metadata?.affects_je ?? false,
            divergence_notes: e.metadata?.divergence_notes,
          });
        }
      }

      setAllMappings(allMap);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sort
  const sorted = [...allMappings].sort((a: any, b: any) => {
    const av = (a[sortCol] ?? '').toString().toLowerCase();
    const bv = (b[sortCol] ?? '').toString().toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const si = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  if (loading) return <p style={{ color: 'var(--text-secondary)', padding: 20 }}>Loading framework analysis...</p>;

  return (
    <div>
      {/* Divergent Treatment Cards */}
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>
        Divergent Treatments ({divergent.length})
      </div>
      {divergent.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 12 }}>No divergent treatments found.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 12, marginBottom: 28 }}>
          {divergent.map((m, i) => (
            <div key={i} style={{
              padding: 16, borderRadius: 8,
              background: 'var(--bg-surface)', border: '1px solid #FB923C40',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 16 }}>&#x1F536;</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textTransform: 'uppercase' }}>
                  {(m.us_gaap_element || '').replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, padding: 10, background: 'rgba(37,99,235,0.06)', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#93C5FD', marginBottom: 4 }}>US GAAP</div>
                  <div style={{ ...mono, fontSize: 11, color: 'var(--text-secondary)' }}>{m.us_gaap_element}</div>
                </div>
                <div style={{ flex: 1, padding: 10, background: 'rgba(74,222,128,0.06)', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#4ADE80', marginBottom: 4 }}>IFRS</div>
                  <div style={{ ...mono, fontSize: 11, color: 'var(--text-secondary)' }}>{m.ifrs_element}</div>
                </div>
              </div>
              {m.divergence_notes && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, fontStyle: 'italic' }}>
                  {m.divergence_notes}
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                  background: '#F8717120', color: '#F87171',
                }}>Affects JE</span>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                  background: '#FB923C20', color: '#FB923C',
                }}>{m.mapping_type}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full Mapping Table */}
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>
        All Cross-Framework Mappings ({allMappings.length})
      </div>
      <div style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
          <thead>
            <tr>
              <th style={th} onClick={() => toggleSort('us_gaap_element')}>US GAAP Concept{si('us_gaap_element')}</th>
              <th style={th} onClick={() => toggleSort('ifrs_element')}>IFRS Concept{si('ifrs_element')}</th>
              <th style={th} onClick={() => toggleSort('mapping_type')}>Type{si('mapping_type')}</th>
              <th style={th} onClick={() => toggleSort('affects_je')}>Affects JE{si('affects_je')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m, i) => {
              const isDivergent = m.affects_je;
              const isExpanded = expandedIdx === i;
              return (
                <React.Fragment key={i}>
                  <tr
                    onClick={() => setExpandedIdx(isExpanded ? null : i)}
                    style={{ cursor: m.divergence_notes ? 'pointer' : 'default' }}
                  >
                    <td style={{ ...td(i), ...mono, fontSize: 11, color: '#93C5FD' }}>{m.us_gaap_element}</td>
                    <td style={{ ...td(i), ...mono, fontSize: 11, color: '#4ADE80' }}>{m.ifrs_element}</td>
                    <td style={{ ...td(i), fontSize: 11 }}>{m.mapping_type}</td>
                    <td style={td(i)}>
                      {isDivergent ? (
                        <span style={{ color: '#F87171', fontWeight: 600, fontSize: 11 }}>Yes</span>
                      ) : (
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>No</span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && m.divergence_notes && (
                    <tr>
                      <td colSpan={4} style={{ padding: '10px 16px', background: 'rgba(251,146,60,0.04)', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        {m.divergence_notes}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {allMappings.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
            No cross-framework mappings found.
          </div>
        )}
      </div>
    </div>
  );
}
